import { computed, ref } from 'vue'
import axios from 'axios'

const normalizeRefKey = (ref) => ref?.refKey ?? ref?.ref_key ?? ref?.key ?? ''
const normalizeRefName = (ref) => ref?.refName ?? ref?.ref_name ?? ref?.name ?? ''

export const formatReferenceDefLabel = (ref) => {
  const parts = []
  const key = normalizeRefKey(ref)
  const name = normalizeRefName(ref)
  if (key) parts.push(key)
  if (name) parts.push(name)
  return parts.join(' - ') || key || name || (ref?.id ? String(ref.id) : '')
}

export const formatReferenceDataLabel = (item) => {
  if (!item) return ''
  const key = item.refKey || item.ref_key || item.key || item.code || item.value
  const name = item.name || item.title || item.label || item.refName || item.ref_name
  if (name && key) return `${name} (${key})`
  if (name) return name
  if (key) return String(key)
  const secondary = item.component || item.type
  return [secondary].filter(Boolean).join(' / ') || String(item.id || '')
}

export const isReferenceFieldType = (field) => {
  const tp = String(field?.type || field?.dataType || field?.formType || '').toLowerCase()
  return ['object', 'ref', 'reference', '对象'].includes(tp)
}

const isDictionaryApi = (def) => def?.raw?.apiUrl?.includes('/api/dictionaries/')

const getDictionaryKey = (item) => {
  if (item?.dictKey !== undefined && item.dictKey !== null) return item.dictKey
  if (item?.dict_key !== undefined && item.dict_key !== null) return item.dict_key
  if (item?.key !== undefined && item.key !== null) return item.key
  if (item?.dictKey === 0) return 0
  return undefined
}

/**
 * 元数据参照：加载 bymy_meta_reference 定义与选项，表单下拉 + 列表 ID→名称 共用。
 * 字段在表单配置里设 type=object/ref + refKey 即可，无需各页面单独写。
 */
export function useMetaReference() {
  const referenceDefs = ref([])
  const referenceDataMap = ref({})
  const referenceLoading = ref(false)
  const fieldRefKeyMap = ref({})

  const referenceDefMap = computed(() => {
    const map = {}
    referenceDefs.value.forEach((item) => {
      if (item.key) map[item.key] = item
    })
    return map
  })

  const findReferenceDefinition = (key) => referenceDefMap.value[key]

  const loadReferenceDefinitions = async () => {
    if (referenceLoading.value) return
    referenceLoading.value = true
    try {
      const res = await axios.post('/api/meta/references/list', { status: 1 })
      const list = (res.data && res.data.data) || res.data || []
      referenceDefs.value = (Array.isArray(list) ? list : []).map((ref) => ({
        key: normalizeRefKey(ref) || (ref?.id ? String(ref.id) : ''),
        label: formatReferenceDefLabel(ref),
        raw: ref,
      }))
    } catch (e) {
      console.warn('加载参照定义失败', e)
    } finally {
      referenceLoading.value = false
    }
  }

  const ensureReferenceDefinitions = async () => {
    if (!referenceDefs.value.length) await loadReferenceDefinitions()
  }

  const loadReferenceEntries = async (def) => {
    if (!def || !def.raw?.apiUrl) return []
    const cacheKey = def.key
    if (!cacheKey) return []
    const dictionary = isDictionaryApi(def)
    if (!dictionary && referenceDataMap.value[cacheKey]?.length > 0) {
      return referenceDataMap.value[cacheKey]
    }
    try {
      const res = await axios.get(def.raw.apiUrl)
      const raw = (res.data && res.data.data) || res.data || []
      const records = Array.isArray(raw?.records) ? raw.records : Array.isArray(raw) ? raw : []
      const filtered = dictionary
        ? records.filter((item) => {
          const key = getDictionaryKey(item)
          if (key === undefined || key === null) return true
          const parsed = Number(key)
          return Number.isNaN(parsed) ? true : parsed >= 1
        })
        : records
      const options = filtered
        .map((item) => {
          const dictKey = dictionary ? getDictionaryKey(item) : undefined
          const fallback = dictionary
            ? item.id ?? item.value ?? item.key ?? item.refKey ?? item.ref_key
            : item.id ?? item.value ?? item.key ?? item.code ?? item.refKey ?? item.ref_key
          const value = dictKey !== undefined && dictKey !== null ? dictKey : fallback
          return {
            label: formatReferenceDataLabel(item),
            value: value !== undefined && value !== null ? String(value) : '',
          }
        })
        .filter((item) => item.value)
      if (options.length > 0) {
        referenceDataMap.value = { ...referenceDataMap.value, [cacheKey]: options }
      }
      return options
    } catch (e) {
      console.warn('加载参照数据失败', def.raw?.apiUrl, e)
      return []
    }
  }

  const loadByRefKey = async (refKey) => {
    if (!refKey) return []
    await ensureReferenceDefinitions()
    const def = findReferenceDefinition(refKey)
    if (!def) return []
    return loadReferenceEntries(def)
  }

  const setupFieldRefKeyMap = (formFields = [], columns = []) => {
    const map = {}
    ;(formFields || []).forEach((f) => {
      const prop = f.prop || f.field
      if (!prop) return
      if (isReferenceFieldType(f) && f.refKey) map[prop] = f.refKey
    })
    ;(columns || []).forEach((c) => {
      const prop = c.prop || c.field
      if (c.refKey && prop) map[prop] = c.refKey
    })
    fieldRefKeyMap.value = map
    return map
  }

  const preloadReferenceKeys = async (refKeys = []) => {
    await ensureReferenceDefinitions()
    const keys = [...new Set((refKeys || []).filter(Boolean))]
    await Promise.all(
      keys.map(async (key) => {
        const def = findReferenceDefinition(key)
        if (def) await loadReferenceEntries(def)
      })
    )
  }

  const applyReferenceOptionsForFields = async (fields = []) => {
    await ensureReferenceDefinitions()
    await Promise.all(
      (fields || []).map(async (field) => {
        if (!field || !isReferenceFieldType(field)) return
        const key = field.refKey || field.ref_key
        if (!key) return
        const data = await loadByRefKey(key)
        if (Array.isArray(data) && data.length) {
          field.options = data
        }
      })
    )
  }

  const preloadForFormAndList = async (formFields = [], columns = []) => {
    setupFieldRefKeyMap(formFields, columns)
    const keys = new Set(Object.values(fieldRefKeyMap.value))
    ;(columns || []).forEach((c) => {
      if (c.refKey) keys.add(c.refKey)
    })
    await applyReferenceOptionsForFields(formFields)
    await preloadReferenceKeys([...keys])
  }

  const isReferenceColumn = (col) => {
    if (!col) return false
    if (col.refKey || fieldRefKeyMap.value[col.prop]) return true
    return isReferenceFieldType(col)
  }

  const resolveRefLabel = (fieldOrCol, value) => {
    if (value === undefined || value === null || value === '') return '-'
    const prop = typeof fieldOrCol === 'string' ? fieldOrCol : fieldOrCol?.prop
    const refKey =
      typeof fieldOrCol === 'string'
        ? fieldRefKeyMap.value[fieldOrCol]
        : fieldOrCol?.refKey || fieldRefKeyMap.value[prop]
    if (!refKey) return value
    const options = referenceDataMap.value[refKey] || []
    const found = options.find((o) => String(o.value) === String(value))
    return found ? found.label : value
  }

  const getOptionsByRefKey = (refKey) => referenceDataMap.value[refKey] || []

  return {
    referenceDefs,
    referenceDataMap,
    referenceLoading,
    fieldRefKeyMap,
    loadReferenceDefinitions,
    ensureReferenceDefinitions,
    loadReferenceEntries,
    loadByRefKey,
    findReferenceDefinition,
    setupFieldRefKeyMap,
    preloadReferenceKeys,
    applyReferenceOptionsForFields,
    preloadForFormAndList,
    isReferenceFieldType,
    isReferenceColumn,
    resolveRefLabel,
    getOptionsByRefKey,
    formatReferenceDataLabel,
  }
}
